import { itemTemplateApi } from "#/api/itemTemplateApi";
import ActionButton from "#/components/buttons/ActionButton";
import Pagination from "#/components/buttons/Pagination";
import { showSuccessNotification } from "#/components/notifications";
import BaseTable, { type TableSortProps } from "#/components/tables/BaseTable";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import type { ItemTemplate } from "#/types/itemTemplate";
import { Button, Modal, Table, TextInput, Title } from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import { IconEye, IconPlus, IconSearch, IconTrash } from "@tabler/icons-react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useClient } from "#/hook/useClient";
import { formatDate, formatTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const TemplatesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite, canDelete } = usePermission();
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(25);
  const [sortDirection, setSortDirection] = useState<"ASC" | "DESC">("DESC");
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [templateToDelete, setTemplateToDelete] = useState<ItemTemplate>();
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] =
    useDisclosure(false);

  const headers = [
    { key: "name", value: t("templates.columns.name") },
    { key: "description", value: t("templates.columns.description") },
    { key: "product", value: t("templates.columns.category") },
    { key: "site", value: t("templates.columns.site") },
    { key: "location", value: t("templates.columns.location") },
    { key: "createdAt", value: t("templates.columns.createdAt") },
    { key: "actions", value: t("common.actions") },
  ];
  const [sortBy, setSortBy] = useState<string>("createdAt");

  const {
    data: { templates = [], total = 0, per_page = 0 } = {},
    isPending,
    refetch: getTemplates,
  } = useQuery({
    queryKey: ["item-templates", page, limit, sortDirection, sortBy, debouncedSearch],
    queryFn: async () => {
      const response = await itemTemplateApi.getAll({
        page: page,
        limit: limit,
        sortBy: sortBy,
        order: sortDirection,
        ...(debouncedSearch ? { search: debouncedSearch } : {}),
      });
      if (!response.data) {
        throw new Error("Failed to fetch templates");
      }
      return {
        templates: response.data.data,
        total: response.data.total,
        per_page: response.data.limit,
      };
    },
    staleTime: 1000 * 60,
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      return await itemTemplateApi.delete(id);
    },
    onSuccess: () => {
      showSuccessNotification({
        message: t("templates.deleteTemplate.success"),
      });
      closeDeleteModal();
      setTemplateToDelete(undefined);
      void getTemplates();
    },
    onError: (error) => {
      console.log("🚀 ~ deleteTemplate ~ error:", error);
    },
  });

  return (
    <div className="w-full h-full flex flex-col overflow-hidden gap-4">
      <div className="flex flex-col gap-3 bg-white z-10">
        <div className="flex items-center justify-between">
          <Title order={2}>{t("templates.title")}</Title>
          <div className="w-fit flex items-center justify-center gap-2">
            <PermissionTooltip disabled={!canWrite()}>
              <Button
                leftSection={<IconPlus size={18} />}
                onClick={() => {
                  void navigate("/inventory/templates/new");
                }}
                color="black"
                size="sm"
                disabled={!canWrite()}
              >
                {t("templates.createTemplate")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>

        <div className="flex items-center gap-2 justify-between">
          <TextInput
            className="flex-1 max-w-96"
            placeholder={t("templates.searchPlaceholder")}
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              setPage(1);
            }}
          />
          <div className="flex items-center gap-2 ml-auto">
            <Pagination
              page={page}
              total={total}
              limit={per_page}
              onChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
            />
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <BaseTable
          headers={headers.map((item) => item.value)}
          loading={isPending}
          allowSorting={true}
          sortableColumns={[0,1,2,3,4,5]}
          onSortChange={(e: TableSortProps) => {
            setPage(1);
            setSortDirection(e.direction);
            setSortBy(headers[e.index].key);
          }}
          defaultSort={{
            direction: "DESC",
            index: 6,
          }}
          rows={templates.map((template) => {
            return (
              <Table.Tr key={template.id}>
                <Table.Td style={{ minWidth: '180px' }}>{template.name ?? "--"}</Table.Td>
                <Table.Td className="max-w-xs truncate" style={{ minWidth: '250px' }} title={template.description || ""}>
                  {template.description ?? "--"}
                </Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{template.product?.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '150px' }}>{template.site?.name ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '200px' }}>{template.location ?? "--"}</Table.Td>
                <Table.Td style={{ minWidth: '180px' }}>
                  {template.createdAt ? (
                    <span>
                      {formatDate(template.createdAt, client)}
                      <br />
                      {formatTime(template.createdAt, client)}
                    </span>
                  ) : (
                    "--"
                  )}
                </Table.Td>
                <Table.Td style={{ minWidth: '120px' }}>
                  <div className="w-full flex items-center justify-center gap-2">
                    <ActionButton
                      icon={IconEye}
                      variant="outline"
                      onClick={() => {
                        void navigate(`/inventory/templates/${template.id}`);
                      }}
                    />
                    <PermissionTooltip disabled={!canDelete()}>
                      <ActionButton
                        icon={IconTrash}
                        variant="outline"
                        color="red"
                        onClick={() => {
                          setTemplateToDelete(template);
                          openDeleteModal();
                        }}
                        disabled={!canDelete()}
                      />
                    </PermissionTooltip>
                  </div>
                </Table.Td>
              </Table.Tr>
            );
          })}
        />
      </div>

      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        centered
        title={t("templates.deleteTemplate.title")}
        closeOnClickOutside={false}
        classNames={{
          title: "font-bold",
        }}
      >
        <div className="w-full flex flex-col items-center justify-center gap-4">
          <div className="flex flex-col gap-2 text-sm">
            <span>{t("templates.deleteTemplate.description")}</span>
            <div className="flex flex-col gap-1 mx-auto my-4 bg-slate-100 border border-dashed border-slate-400 rounded-lg p-4 w-full text-center">
              <span className="font-bold">{templateToDelete?.name ?? "--"}</span>
              {templateToDelete?.description && (
                <span className="text-xs text-gray-600">
                  {templateToDelete.description}
                </span>
              )}
            </div>
            <span>
              {t("templates.deleteTemplate.warning")}
            </span>

            <span className="font-bold my-2">
              {t("templates.deleteTemplate.confirmation")}
            </span>
          </div>

          <div className="w-full grid grid-cols-[auto_1fr] items-center gap-2">
            <Button size="sm" color="black" onClick={closeDeleteModal}>
              {t("common.cancel")}
            </Button>
            <PermissionTooltip disabled={!canDelete()}>
              <Button
                onClick={() => {
                  if (templateToDelete) {
                    deleteTemplateMutation.mutate(templateToDelete.id);
                  }
                }}
                color="red"
                size="sm"
                loading={deleteTemplateMutation.isPending}
                leftSection={<IconTrash size={18} />}
                disabled={!canDelete()}
              >
                {t("templates.deleteTemplate.confirmButton")}
              </Button>
            </PermissionTooltip>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TemplatesPage;
